import React from 'react';
import { Leaf, Target, Eye, Award, Users, Globe, TrendingUp } from 'lucide-react';
import About from '../components/About';
import { aboutFeatures } from '../mock';

const AboutPage = () => {
  const values = [
    { icon: Target, title: 'Our Mission', desc: 'Accelerate India\'s transition to clean fuel by transforming every ton of agricultural waste into renewable energy.' },
    { icon: Eye, title: 'Our Vision', desc: 'A self-reliant, carbon-neutral Bharat powered entirely by indigenous, renewable energy sources by 2050.' },
    { icon: Award, title: 'Our Values', desc: 'Sustainability, innovation, community impact, and uncompromising quality across every project we deliver.' },
  ];

  const milestones = [
    { year: '2013', text: 'Founded with our first pilot CBG plant in Uttar Pradesh.' },
    { year: '2016', text: 'Crossed 10 operational plants and signed first SATAT MOU.' },
    { year: '2019', text: 'Expanded into Bio-Diesel and Bio-Ethanol verticals.' },
    { year: '2022', text: 'Launched EcoSolar division — 1 GW installation milestone.' },
    { year: '2024', text: 'Inaugurated India\'s largest waste-to-energy facility in Maharashtra.' },
    { year: '2025', text: 'Pioneering Green Hydrogen pilot programs across 4 states.' },
  ];

  return (
    <div>
      <PageHero title="About Us" subtitle="Pioneering India's Clean Fuel Revolution" />

      <About />

      <section className="py-24 bg-gradient-to-b from-emerald-50/40 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-200 bg-white text-emerald-700 text-xs font-semibold uppercase tracking-widest">
              <Leaf className="w-3.5 h-3.5" /> Who We Are
            </div>
            <h2 className="mt-5 text-4xl md:text-5xl font-extrabold text-[#0b1a1a] leading-tight">Driven by <span className="text-emerald-600">Purpose</span></h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-3xl p-8 border border-slate-100 hover:border-emerald-200 hover:-translate-y-1 transition-all shadow-sm hover:shadow-xl hover:shadow-emerald-100">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-md shadow-emerald-200">
                  <v.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-[#0b1a1a]">{v.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-widest">Our Journey</div>
            <h2 className="mt-5 text-4xl md:text-5xl font-extrabold text-[#0b1a1a]">A Decade of <span className="text-emerald-600">Innovation</span></h2>
          </div>
          <div className="mt-14 relative max-w-4xl mx-auto">
            <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-200 via-emerald-400 to-emerald-200" />
            {milestones.map((m, i) => (
              <div key={m.year} className={`relative mb-10 flex items-center ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6`}>
                <div className="hidden md:block flex-1" />
                <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 w-4 h-4 rounded-full bg-emerald-500 ring-4 ring-white shadow-md" />
                <div className="flex-1 ml-12 md:ml-0 bg-white border border-slate-100 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="text-emerald-600 font-extrabold text-lg">{m.year}</div>
                  <div className="mt-2 text-slate-700">{m.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0b1a1a] text-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-6 text-center">
          {[{i:Users,v:'500+',l:'Team Members'},{i:Globe,v:'56+',l:'Live Plants'},{i:TrendingUp,v:'2M+',l:'Tons Waste Processed'},{i:Award,v:'30+',l:'Awards Won'}].map((s,k)=>(
            <div key={k} className="p-6">
              <s.i className="w-9 h-9 text-emerald-400 mx-auto" />
              <div className="mt-4 text-4xl font-extrabold">{s.v}</div>
              <div className="mt-1 text-sm text-slate-300 font-semibold uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 bg-emerald-50/40">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="text-3xl md:text-4xl font-extrabold text-[#0b1a1a]">What sets us apart</h3>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {aboutFeatures.map((f) => (
              <div key={f} className="bg-white rounded-xl p-4 border border-emerald-100 font-semibold text-slate-700 text-sm">{f}</div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const PageHero = ({ title, subtitle }) => (
  <section className="relative bg-gradient-to-br from-emerald-50 via-white to-lime-50 py-20 overflow-hidden border-b border-emerald-100">
    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(#10b981 1px, transparent 1px), linear-gradient(90deg, #10b981 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
    <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-emerald-200/40 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-6 text-center">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-200 bg-white text-emerald-700 text-xs font-semibold uppercase tracking-widest">
        <Leaf className="w-3.5 h-3.5" /> Indian Energreen
      </div>
      <h1 className="mt-5 text-5xl md:text-6xl font-extrabold text-[#0b1a1a] leading-tight">{title}</h1>
      {subtitle && <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  </section>
);

export default AboutPage;
